import { FC, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { InlineNotification } from '@carbon/react';
import { SuccessToast } from 'components/success-toast';
import { WellWorkspaceShell } from './detail/page';
import { WellWorkspaceLocationState } from './utils';

/**
 * A Well Workspace at its own URL, `/well_workspaces/:id`, the way the DCA and
 * inventory workspaces have theirs. Every opener navigates here — the Well page's
 * drawer and create, Map Query's listing and create — so the address bar always names
 * the workspace and a link to it can be shared.
 *
 * An opener that knows which well the reader came from names it in router state, so the
 * workspace lands on that well rather than on whichever of its wells sorts first. Back
 * returns to the page that opened it, or to Map Query when the URL was arrived at
 * directly.
 * @return {JSX.Element} The workspace, or a notice when the URL carries no id.
 */
export const WellWorkspacePage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { state } = useLocation<WellWorkspaceLocationState | undefined>();
  const initialUwi = state?.well ?? null;
  // Arriving from Create says so once, in the top-right corner, and clears itself.
  const [createdToast, setCreatedToast] = useState(state?.created === true);

  if (!id) {
    return (
      <InlineNotification
        kind="error"
        title="Missing workspace id"
        lowContrast
      />
    );
  }

  return (
    <>
      <WellWorkspaceShell
        id={id}
        initialUwi={initialUwi}
        onLeave={() => {
          // A fresh tab or a pasted link has nothing behind it to go back to.
          if (history.length > 1) history.goBack();
          else history.push('/map-query');
        }}
        backLabel="Back"
      />
      {createdToast && (
        <SuccessToast
          message="Workspace created"
          onClose={() => {
            setCreatedToast(false);
            return true;
          }}
        />
      )}
    </>
  );
};
